import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { X, Save, Plus, Trash2, AlertCircle, XCircle, MessageSquare, UserPlus } from 'lucide-react';
import { StatusBadge, ALL_STATUSES } from '../shared/StatusBadge';
import { productLabel, getForeclosureName } from './loanFieldConstants';
import axios from 'axios';

// Import the form components from NewApplicationModal
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatSSN = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const EditApplicationModal = ({ application, onClose, onSuccess, onOpenMessages }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [status, setStatus] = useState(application.status || 'submitted');
  const [declineReason, setDeclineReason] = useState(application.decline_reason || '');
  const [missingDocs, setMissingDocs] = useState(application.missing_documents || []);
  const [newDoc, setNewDoc] = useState('');

  useEffect(() => {
    // Initialize form data based on application type.
    //
    // Critical: depend on application.id (a stable string), NOT the whole
    // application object. The parent dashboard polls/refetches every 15s,
    // which creates a new application reference each time. Depending on the
    // object reference here was wiping the user's in-progress edits back to
    // the original values on every poll — looking exactly like "the input
    // won't let me type" because the keystroke was immediately overwritten.
    const pt = application.product_type;
    const dataKey = `${pt}_data`;
    setFormData({
      product_type: pt,
      [dataKey]: application[dataKey] || {}
    });
  }, [application.id]);

  const handleSave = async () => {
    setLoading(true);
    try {
      // Save form data
      await axios.put(
        `${BACKEND_URL}/api/applications/${application.id}`,
        formData
      );

      // Save status if changed
      if (status !== application.status) {
        let statusUrl = `${BACKEND_URL}/api/applications/${application.id}/status?status=${status}`;
        if (status === 'declined' && declineReason) {
          statusUrl += `&decline_reason=${encodeURIComponent(declineReason)}`;
        }
        await axios.put(statusUrl);
      } else if (status === 'declined' && declineReason !== (application.decline_reason || '')) {
        // Status didn't change but decline reason was updated
        await axios.put(
          `${BACKEND_URL}/api/applications/${application.id}/decline-reason`,
          { decline_reason: declineReason }
        );
      }

      // Save missing documents if changed
      const originalDocs = application.missing_documents || [];
      if (JSON.stringify(missingDocs) !== JSON.stringify(originalDocs)) {
        await axios.put(
          `${BACKEND_URL}/api/applications/${application.id}/missing-documents`,
          { missing_documents: missingDocs }
        );
      }

      onSuccess();
    } catch (error) {
      console.error('Error updating application:', error);
      alert('Error updating application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addMissingDoc = () => {
    const doc = newDoc.trim();
    if (doc && !missingDocs.includes(doc)) {
      setMissingDocs(prev => [...prev, doc]);
      setNewDoc('');
    }
  };

  const removeMissingDoc = (index) => {
    setMissingDocs(prev => prev.filter((_, i) => i !== index));
  };

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const getBusinessName = () => {
    const dataKey = `${application.product_type}_data`;
    const data = application[dataKey] || {};
    return data.legal_corporate_name || data.business_legal_name || data.business_name || data.company_legal_name || data.company_dba ||
           getForeclosureName(application) ||
           (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : null) ||
           data.first_name || 'Business Application';
  };

  const renderFormFields = () => {
    const pt = application.product_type;
    const dataKey = `${pt}_data`;
    const data = formData[dataKey];
    if (pt === 'term_loan') {
      return <TermLoanEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'term_loan_10yr') {
      return <TermLoan10yrEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'term_loc_hybrid') {
      return <TermLocHybridEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'term_loan_3_5yr') {
      return <GrowthTermLoanEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'sba_loan') {
      return <SbaLoanEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'line_of_credit') {
      return <LineOfCreditEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'equipment_financing') {
      return <EquipmentFinancingEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'merchant_processing') {
      return <MerchantProcessingEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'jumbo_term_loan') {
      return <JumboTermLoanEditForm data={data} updateData={updateFormData} />;
    } else if (pt === 'transportation_factoring') {
      return <TransportationFactoringEditForm data={data} updateData={updateFormData} />;
    }
    return <GenericEditForm data={data} dataKey={dataKey} updateData={updateFormData} />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Edit Application</h2>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-slate-600">{getBusinessName()}</span>
              <Badge variant="outline">{productLabel(application.product_type).toUpperCase()}</Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onOpenMessages && (
              <button
                onClick={() => { onClose(); onOpenMessages(application.id); }}
                className="h-9 px-3 flex items-center gap-1.5 rounded-lg border-2 border-[#0E333F] text-[#0E333F] hover:bg-[#0E333F] hover:text-white text-sm font-semibold transition-all"
                data-testid="edit-open-messages"
              >
                <MessageSquare className="h-4 w-4" />
                Messages
              </button>
            )}
            <button onClick={onClose} disabled={loading} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200" data-testid="close-edit-modal">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Status & Missing Documents Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Status Change */}
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm">Application Status</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="flex items-center gap-3">
                  <StatusBadge status={status} />
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="flex-1" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value} data-testid={`status-option-${s.value}`}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Decline Reason - shown when status is declined */}
            {status === 'declined' && (
              <Card className="border-red-200">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                    <XCircle className="h-4 w-4" />
                    Decline Reason
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    placeholder="Enter the reason for declining this application..."
                    className="w-full min-h-[100px] p-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-red-300 resize-y"
                    data-testid="decline-reason-input"
                  />
                  {application.declined_by && (
                    <p className="text-xs text-slate-500 mt-2">
                      Last updated by {application.declined_by}
                      {application.declined_at && ` on ${new Date(application.declined_at).toLocaleDateString()}`}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Missing Documents */}
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-500" />
                  Missing Documents ({missingDocs.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2">
                  {missingDocs.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-md text-sm" data-testid={`missing-doc-${i}`}>
                      <span className="text-orange-800">{doc}</span>
                      <button onClick={() => removeMissingDoc(i)} className="text-orange-400 hover:text-red-500 flex-shrink-0">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <Input
                      value={newDoc}
                      onChange={(e) => setNewDoc(e.target.value)}
                      placeholder="Enter document name..."
                      className="flex-1 h-8 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && addMissingDoc()}
                      data-testid="missing-doc-input"
                    />
                    <Button variant="outline" size="sm" onClick={addMissingDoc} disabled={!newDoc.trim()} className="h-8" data-testid="add-missing-doc-btn">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {renderFormFields()}

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Term Loan Edit Form
const EMPTY_OWNER = {
  first_name: '',
  last_name: '',
  first_last_name: '', // legacy
  date_of_birth: '',
  social_security_number: '',
  mobile_phone_number: '',
  email_address: '',
  home_address: '',
  percentage_of_ownership: 0
};

const AdditionalOwnersEditor = ({ dataKey, data, updateData }) => {
  const owners = data?.[dataKey]?.additional_owners || [];

  const setOwners = (newOwners) => {
    updateData(`${dataKey}.additional_owners`, newOwners);
  };

  const addOwner = () => {
    setOwners([...owners, { ...EMPTY_OWNER }]);
  };

  const removeOwner = (index) => {
    setOwners(owners.filter((_, i) => i !== index));
  };

  const updateOwner = (index, field, value) => {
    const updated = owners.map((o, i) => i === index ? { ...o, [field]: value } : o);
    setOwners(updated);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span>Additional Owners / Guarantors</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addOwner}
            className="text-xs"
            data-testid="add-owner-btn"
          >
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Add Owner
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {owners.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No additional owners. Click "Add Owner" to add one.</p>
        ) : (
          <div className="space-y-4">
            {owners.map((owner, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-4 relative" data-testid={`owner-card-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-slate-600">Owner {idx + 1}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removeOwner(idx)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                    data-testid={`remove-owner-${idx}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">First Name</Label>
                    <Input
                      value={owner.first_name || ''}
                      onChange={(e) => updateOwner(idx, 'first_name', e.target.value)}
                      placeholder="First Name"
                      data-testid={`owner-first-name-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Last Name</Label>
                    <Input
                      value={owner.last_name || ''}
                      onChange={(e) => updateOwner(idx, 'last_name', e.target.value)}
                      placeholder="Last Name"
                      data-testid={`owner-last-name-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Ownership %</Label>
                    <Input
                      type="number"
                      value={owner.percentage_of_ownership || ''}
                      onChange={(e) => updateOwner(idx, 'percentage_of_ownership', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid={`owner-pct-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={owner.email_address || ''}
                      onChange={(e) => updateOwner(idx, 'email_address', e.target.value)}
                      placeholder="email@example.com"
                      data-testid={`owner-email-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={owner.mobile_phone_number || ''}
                      onChange={(e) => updateOwner(idx, 'mobile_phone_number', e.target.value)}
                      placeholder="Phone number"
                      data-testid={`owner-phone-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date of Birth</Label>
                    <Input
                      value={owner.date_of_birth || ''}
                      onChange={(e) => updateOwner(idx, 'date_of_birth', e.target.value)}
                      placeholder="MM/DD/YYYY"
                      data-testid={`owner-dob-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">SSN</Label>
                    <Input
                      value={owner.social_security_number || ''}
                      onChange={(e) => updateOwner(idx, 'social_security_number', e.target.value)}
                      placeholder="XXX-XX-XXXX"
                      data-testid={`owner-ssn-${idx}`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Home Address</Label>
                    <Input
                      value={owner.home_address || ''}
                      onChange={(e) => updateOwner(idx, 'home_address', e.target.value)}
                      placeholder="Full home address"
                      data-testid={`owner-address-${idx}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TermLoanEditForm = ({ data, updateData }) => {
  return (
    <div className="space-y-6">
      {/* Primary Borrower Information */}
      <Card>
        <CardHeader>
          <CardTitle>Primary Borrower Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={data?.first_name || ''}
                onChange={(e) => updateData('term_loan_data.first_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={data?.last_name || ''}
                onChange={(e) => updateData('term_loan_data.last_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="email_address">Email Address</Label>
              <Input
                id="email_address"
                type="email"
                value={data?.email_address || ''}
                onChange={(e) => updateData('term_loan_data.email_address', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                value={data?.phone_number || ''}
                onChange={(e) => updateData('term_loan_data.phone_number', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="social_security_number">Social Security Number</Label>
              <Input
                id="social_security_number"
                value={formatSSN(data?.social_security_number)}
                onChange={(e) => updateData('term_loan_data.social_security_number', formatSSN(e.target.value))}
                placeholder="XXX-XX-XXXX"
              />
            </div>
            <div>
              <Label htmlFor="ownership_percentage">Percentage of Ownership</Label>
              <Input
                id="ownership_percentage"
                type="number"
                min="0"
                max="100"
                value={data?.ownership_percentage || ''}
                onChange={(e) => updateData('term_loan_data.ownership_percentage', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="home_address">Home Address</Label>
            <Input
              id="home_address"
              value={data?.home_address || ''}
              onChange={(e) => updateData('term_loan_data.home_address', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="home_city">City</Label>
              <Input
                id="home_city"
                value={data?.home_city || ''}
                onChange={(e) => updateData('term_loan_data.home_city', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="home_state">State</Label>
              <Input
                id="home_state"
                value={data?.home_state || ''}
                onChange={(e) => updateData('term_loan_data.home_state', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="home_zip">ZIP</Label>
              <Input
                id="home_zip"
                value={data?.home_zip || ''}
                onChange={(e) => updateData('term_loan_data.home_zip', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card>
        <CardHeader>
          <CardTitle>Business Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="legal_corporate_name">Legal / Corporate Name</Label>
              <Input
                id="legal_corporate_name"
                value={data?.legal_corporate_name || ''}
                onChange={(e) => updateData('term_loan_data.legal_corporate_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_address">Business Address</Label>
              <Input
                id="business_address"
                value={data?.business_address || ''}
                onChange={(e) => updateData('term_loan_data.business_address', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_city">City</Label>
              <Input
                id="business_city"
                value={data?.business_city || ''}
                onChange={(e) => updateData('term_loan_data.business_city', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_state">State</Label>
              <Input
                id="business_state"
                value={data?.business_state || ''}
                onChange={(e) => updateData('term_loan_data.business_state', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_zip">ZIP</Label>
              <Input
                id="business_zip"
                value={data?.business_zip || ''}
                onChange={(e) => updateData('term_loan_data.business_zip', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="federal_tax_id">Federal Tax ID (EIN)</Label>
              <Input
                id="federal_tax_id"
                value={data?.federal_tax_id || ''}
                onChange={(e) => updateData('term_loan_data.federal_tax_id', e.target.value)}
                placeholder="XX-XXXXXXX"
              />
            </div>
            <div>
              <Label htmlFor="business_phone">Business Phone</Label>
              <Input
                id="business_phone"
                value={data?.business_phone || ''}
                onChange={(e) => updateData('term_loan_data.business_phone', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="years_in_business">Years in Business</Label>
              <Input
                id="years_in_business"
                value={data?.years_in_business || ''}
                onChange={(e) => updateData('term_loan_data.years_in_business', e.target.value)}
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={data?.industry || ''}
                onChange={(e) => updateData('term_loan_data.industry', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="business_description">Business Description</Label>
            <Input
              id="business_description"
              value={data?.business_description || ''}
              onChange={(e) => updateData('term_loan_data.business_description', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Loan Information */}
      <Card>
        <CardHeader>
          <CardTitle>Loan Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="funding_amount_requested">Funding Amount Requested</Label>
              <Input
                id="funding_amount_requested"
                type="number"
                value={data?.funding_amount_requested || ''}
                onChange={(e) => updateData('term_loan_data.funding_amount_requested', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="annual_revenue">Annual Revenue</Label>
              <Input
                id="annual_revenue"
                type="number"
                value={data?.annual_revenue || ''}
                onChange={(e) => updateData('term_loan_data.annual_revenue', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="monthly_cash_flow">Monthly Cash Flow</Label>
              <Input
                id="monthly_cash_flow"
                type="number"
                value={data?.monthly_cash_flow || ''}
                onChange={(e) => updateData('term_loan_data.monthly_cash_flow', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="credit_score">Personal Credit Score (estimate)</Label>
              <Input
                id="credit_score"
                type="number"
                value={data?.credit_score || ''}
                onChange={(e) => updateData('term_loan_data.credit_score', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="use_of_funds">Intended Use of Funds</Label>
            <Input
              id="use_of_funds"
              value={data?.use_of_funds || ''}
              onChange={(e) => updateData('term_loan_data.use_of_funds', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
      <AdditionalOwnersEditor dataKey="term_loan_data" data={data} updateData={updateData} />
    </div>
  );
};

// SBA Loan Edit Form
const SbaLoanEditForm = ({ data, updateData }) => {
  return (
    <div className="space-y-6">
      {/* Business Information */}
      <Card>
        <CardHeader>
          <CardTitle>Business Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="business_legal_name">Business Legal Name</Label>
              <Input
                id="business_legal_name"
                value={data?.business_legal_name || ''}
                onChange={(e) => updateData('sba_loan_data.business_legal_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="doing_business_as">Doing Business As (DBA)</Label>
              <Input
                id="doing_business_as"
                value={data?.doing_business_as || ''}
                onChange={(e) => updateData('sba_loan_data.doing_business_as', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="legal_entity_type">Legal Entity Type</Label>
              <Select value={data?.legal_entity_type || ''} onValueChange={(value) => updateData('sba_loan_data.legal_entity_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select entity type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sole Proprietorship">Sole Proprietorship</SelectItem>
                  <SelectItem value="Partnership">Partnership</SelectItem>
                  <SelectItem value="LLC">LLC</SelectItem>
                  <SelectItem value="Corporation">Corporation</SelectItem>
                  <SelectItem value="S Corporation">S Corporation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="business_start_date">Business Start Date</Label>
              <Input
                id="business_start_date"
                type="date"
                value={data?.business_start_date || ''}
                onChange={(e) => updateData('sba_loan_data.business_start_date', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="federal_tax_id">Federal Tax ID (EIN)</Label>
              <Input
                id="federal_tax_id"
                value={data?.federal_tax_id || ''}
                onChange={(e) => updateData('sba_loan_data.federal_tax_id', e.target.value)}
                placeholder="XX-XXXXXXX"
              />
            </div>
            <div>
              <Label htmlFor="business_phone_number">Business Phone Number</Label>
              <Input
                id="business_phone_number"
                value={data?.business_phone_number || ''}
                onChange={(e) => updateData('sba_loan_data.business_phone_number', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="naics_code_or_nature">NAICS Code or Nature of Business</Label>
              <Input
                id="naics_code_or_nature"
                value={data?.naics_code_or_nature || ''}
                onChange={(e) => updateData('sba_loan_data.naics_code_or_nature', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="business_address">Business Address</Label>
            <Input
              id="business_address"
              value={data?.business_address || ''}
              onChange={(e) => updateData('sba_loan_data.business_address', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="number_of_employees">Number of Employees</Label>
              <Input
                id="number_of_employees"
                type="number"
                min="0"
                value={data?.number_of_employees ?? ''}
                onChange={(e) => updateData('sba_loan_data.number_of_employees', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                placeholder="e.g., 12"
                data-testid="edit-sba-number-of-employees"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="business_city">City</Label>
              <Input
                id="business_city"
                value={data?.business_city || ''}
                onChange={(e) => updateData('sba_loan_data.business_city', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_state">State</Label>
              <Input
                id="business_state"
                value={data?.business_state || ''}
                onChange={(e) => updateData('sba_loan_data.business_state', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_zip">ZIP</Label>
              <Input
                id="business_zip"
                value={data?.business_zip || ''}
                onChange={(e) => updateData('sba_loan_data.business_zip', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loan Information */}
      <Card>
        <CardHeader>
          <CardTitle>Loan Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="loan_requested_amount">Loan Requested Amount</Label>
              <Input
                id="loan_requested_amount"
                type="number"
                value={data?.loan_requested_amount || ''}
                onChange={(e) => updateData('sba_loan_data.loan_requested_amount', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="total_monthly_sales">Total Monthly Sales</Label>
              <Input
                id="total_monthly_sales"
                type="number"
                value={data?.total_monthly_sales || ''}
                onChange={(e) => updateData('sba_loan_data.total_monthly_sales', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="has_existing_debt">Has Existing Debt?</Label>
              <Select value={data?.has_existing_debt || ''} onValueChange={(value) => updateData('sba_loan_data.has_existing_debt', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="refinance_existing_debt">Will Refinance Existing Debt?</Label>
              <Select value={data?.refinance_existing_debt || ''} onValueChange={(value) => updateData('sba_loan_data.refinance_existing_debt', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Primary Applicant */}
      <Card>
        <CardHeader>
          <CardTitle>Primary Applicant Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={data?.first_name || ''}
                onChange={(e) => updateData('sba_loan_data.first_name', e.target.value)}
                data-testid="edit-sba-first-name"
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={data?.last_name || ''}
                onChange={(e) => updateData('sba_loan_data.last_name', e.target.value)}
                data-testid="edit-sba-last-name"
              />
            </div>
            <div>
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={data?.date_of_birth || ''}
                onChange={(e) => updateData('sba_loan_data.date_of_birth', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="social_security_number">Social Security Number</Label>
              <Input
                id="social_security_number"
                value={formatSSN(data?.social_security_number)}
                onChange={(e) => updateData('sba_loan_data.social_security_number', formatSSN(e.target.value))}
                placeholder="XXX-XX-XXXX"
              />
            </div>
            <div>
              <Label htmlFor="mobile_phone_number">Mobile Phone Number</Label>
              <Input
                id="mobile_phone_number"
                value={data?.mobile_phone_number || ''}
                onChange={(e) => updateData('sba_loan_data.mobile_phone_number', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="email_address">Email Address</Label>
              <Input
                id="email_address"
                type="email"
                value={data?.email_address || ''}
                onChange={(e) => updateData('sba_loan_data.email_address', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="home_address">Home Address</Label>
            <Input
              id="home_address"
              value={data?.home_address || ''}
              onChange={(e) => updateData('sba_loan_data.home_address', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="home_city">City</Label>
              <Input
                id="home_city"
                value={data?.home_city || ''}
                onChange={(e) => updateData('sba_loan_data.home_city', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="home_state">State</Label>
              <Input
                id="home_state"
                value={data?.home_state || ''}
                onChange={(e) => updateData('sba_loan_data.home_state', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="home_zip">ZIP</Label>
              <Input
                id="home_zip"
                value={data?.home_zip || ''}
                onChange={(e) => updateData('sba_loan_data.home_zip', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="additional_notes">Additional Notes</Label>
            <Input
              id="additional_notes"
              value={data?.additional_notes || ''}
              onChange={(e) => updateData('sba_loan_data.additional_notes', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
      <AdditionalOwnersEditor dataKey="sba_loan_data" data={data} updateData={updateData} />
    </div>
  );
};

// Line of Credit Edit Form
const LineOfCreditEditForm = ({ data, updateData }) => {
  return (
    <div className="space-y-6">
      {/* Owner Information */}
      <Card>
        <CardHeader>
          <CardTitle>Owner Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner_first_name">First Name</Label>
              <Input
                id="owner_first_name"
                value={data?.owner_first_name || ''}
                onChange={(e) => updateData('line_of_credit_data.owner_first_name', e.target.value)}
                data-testid="edit-loc-owner-first-name"
              />
            </div>
            <div>
              <Label htmlFor="owner_last_name">Last Name</Label>
              <Input
                id="owner_last_name"
                value={data?.owner_last_name || ''}
                onChange={(e) => updateData('line_of_credit_data.owner_last_name', e.target.value)}
                data-testid="edit-loc-owner-last-name"
              />
            </div>
            <div>
              <Label htmlFor="owner_address">Owner Address</Label>
              <Input
                id="owner_address"
                value={data?.owner_address || ''}
                onChange={(e) => updateData('line_of_credit_data.owner_address', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="owner_ssn">Social Security Number</Label>
              <Input
                id="owner_ssn"
                value={formatSSN(data?.owner_ssn)}
                onChange={(e) => updateData('line_of_credit_data.owner_ssn', formatSSN(e.target.value))}
                placeholder="XXX-XX-XXXX"
              />
            </div>
            <div>
              <Label htmlFor="owner_email">Email</Label>
              <Input
                id="owner_email"
                type="email"
                value={data?.owner_email || ''}
                onChange={(e) => updateData('line_of_credit_data.owner_email', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="owner_phone">Cell Phone</Label>
              <Input
                id="owner_phone"
                value={data?.owner_phone || ''}
                onChange={(e) => updateData('line_of_credit_data.owner_phone', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card>
        <CardHeader>
          <CardTitle>Business Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                value={data?.business_name || ''}
                onChange={(e) => updateData('line_of_credit_data.business_name', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_industry">Industry</Label>
              <Input
                id="business_industry"
                value={data?.business_industry || ''}
                onChange={(e) => updateData('line_of_credit_data.business_industry', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="years_in_business">Years in Business</Label>
              <Input
                id="years_in_business"
                value={data?.years_in_business || ''}
                onChange={(e) => updateData('line_of_credit_data.years_in_business', e.target.value)}
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <Label htmlFor="business_address">Business Address</Label>
              <Input
                id="business_address"
                value={data?.business_address || ''}
                onChange={(e) => updateData('line_of_credit_data.business_address', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_city">City</Label>
              <Input
                id="business_city"
                value={data?.business_city || ''}
                onChange={(e) => updateData('line_of_credit_data.business_city', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_state">State</Label>
              <Input
                id="business_state"
                value={data?.business_state || ''}
                onChange={(e) => updateData('line_of_credit_data.business_state', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_zip_code">Business Zipcode</Label>
              <Input
                id="business_zip_code"
                value={data?.business_zip_code || ''}
                onChange={(e) => updateData('line_of_credit_data.business_zip_code', e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={data?.notes || ''}
                onChange={(e) => updateData('line_of_credit_data.notes', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="business_website_type">Website / Social Media Type</Label>
              <Input
                id="business_website_type"
                value={data?.business_website_type || ''}
                onChange={(e) => updateData('line_of_credit_data.business_website_type', e.target.value)}
                placeholder="e.g., Website, Facebook, Instagram"
              />
            </div>
            <div>
              <Label htmlFor="business_website_link">Website / Social Media Link</Label>
              <Input
                id="business_website_link"
                value={data?.business_website_link || ''}
                onChange={(e) => updateData('line_of_credit_data.business_website_link', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Details */}
      <Card>
        <CardHeader>
          <CardTitle>Line of Credit Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="requested_line_of_credit_amount">Requested Line of Credit Amount</Label>
              <Input
                id="requested_line_of_credit_amount"
                type="number"
                value={data?.requested_line_of_credit_amount || ''}
                onChange={(e) => updateData('line_of_credit_data.requested_line_of_credit_amount', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="purpose_of_credit">Purpose of Credit</Label>
              <Input
                id="purpose_of_credit"
                value={data?.purpose_of_credit || ''}
                onChange={(e) => updateData('line_of_credit_data.purpose_of_credit', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const EquipmentFinancingEditForm = ({ data, updateData }) => {
  const field = (id, label, type = 'text') => {
    const isSSN = SSN_KEYS.has(id);
    return (
      <div key={id}>
        <Label htmlFor={id}>{label}</Label>
        <Input id={id} type={type} value={isSSN ? formatSSN(data?.[id]) : (data?.[id] || '')} onChange={(e) => updateData(`equipment_financing_data.${id}`, isSSN ? formatSSN(e.target.value) : (type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value))} placeholder={isSSN ? 'XXX-XX-XXXX' : undefined} />
      </div>
    );
  };
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-lg">Business Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          {field('business_name', 'Business Name')}
          {field('business_address', 'Address')}
          {field('business_city', 'City')}
          {field('business_state', 'State')}
          {field('business_zip', 'ZIP')}
          {field('entity_type', 'Entity Type')}
          {field('federal_tax_id', 'Tax ID')}
          {field('years_in_business', 'Years in Business')}
          {field('industry', 'Industry')}
          {field('annual_revenue', 'Annual Revenue', 'number')}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg">Owner Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          {field('owner_first_name', 'First Name')}
          {field('owner_last_name', 'Last Name')}
          {field('owner_email', 'Email')}
          {field('owner_phone', 'Phone')}
          {field('owner_ssn', 'SSN')}
          {field('owner_dob', 'Date of Birth')}
          {field('owner_address', 'Home Address')}
          {field('ownership_percentage', 'Ownership %', 'number')}
          {field('fico_score', 'FICO Score', 'number')}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg">Equipment Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          {field('equipment_type', 'Equipment Type')}
          {field('equipment_description', 'Description')}
          {field('equipment_cost', 'Equipment Cost', 'number')}
          {field('new_or_used', 'New or Used')}
          {field('equipment_age_years', 'Age (Years)', 'number')}
          {field('vendor_name', 'Vendor Name')}
          {field('vendor_contact', 'Vendor Contact')}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg">Financing Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          {field('amount_requested', 'Amount Requested', 'number')}
          {field('down_payment', 'Down Payment', 'number')}
          {field('desired_term_months', 'Desired Term (Months)', 'number')}
          {field('use_of_equipment', 'Use of Equipment')}
        </CardContent>
      </Card>
    </div>
  );
};


export default EditApplicationModal;

// Helper to create a standard field renderer
const SSN_KEYS = new Set(['social_security_number', 'owner_ssn', 'ssn_itin']);

const makeField = (data, dataKey, updateData) => (key, label, type = 'text') => {
  const isSSN = SSN_KEYS.has(key);
  return (
    <div key={key}>
      <Label htmlFor={`edit-${key}`}>{label}</Label>
      <Input
        id={`edit-${key}`}
        type={type}
        value={isSSN ? formatSSN(data?.[key]) : (data?.[key] || '')}
        onChange={(e) => updateData(`${dataKey}.${key}`, isSSN ? formatSSN(e.target.value) : e.target.value)}
        placeholder={isSSN ? 'XXX-XX-XXXX' : undefined}
        data-testid={`edit-${key}`}
      />
    </div>
  );
};

const TermLoan10yrEditForm = ({ data, updateData }) => {
  const field = makeField(data, 'term_loan_10yr_data', updateData);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Primary Borrower</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('first_name', 'First Name')}
            {field('last_name', 'Last Name')}
            {field('email_address', 'Email Address', 'email')}
            {field('phone_number', 'Phone Number')}
            {field('social_security_number', 'SSN')}
            {field('date_of_birth', 'Date of Birth')}
            {field('ownership_percentage', 'Ownership %', 'number')}
            {field('home_address', 'Home Address')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Business Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('legal_corporate_name', 'Legal Corporate Name')}
            {field('doing_business_as', 'DBA')}
            {field('business_address', 'Business Address')}
            {field('business_city', 'City')}
            {field('business_state', 'State')}
            {field('business_zip', 'ZIP')}
            {field('entity_type', 'Entity Type')}
            {field('federal_tax_id', 'Federal Tax ID')}
            {field('industry_type', 'Industry')}
            {field('number_of_employees', 'Number of Employees', 'number')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Financial Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('annual_revenue', 'Annual Revenue', 'number')}
            {field('monthly_revenue', 'Monthly Revenue', 'number')}
            {field('funding_amount_requested', 'Funding Requested', 'number')}
            {field('use_of_funds', 'Use of Funds')}
          </div>
        </CardContent>
      </Card>
      <AdditionalOwnersEditor dataKey="term_loan_10yr_data" data={data} updateData={updateData} />
    </div>
  );
};

const TermLocHybridEditForm = ({ data, updateData }) => {
  const field = makeField(data, 'term_loc_hybrid_data', updateData);
  const ld = data?.term_loc_hybrid_data || {};
  // Helper: show one field but pre-fill from either old or new key
  const mergedField = (primaryKey, fallbackKey, label, type) => {
    const val = ld[primaryKey] || ld[fallbackKey] || '';
    const handleChange = (newVal) => {
      updateData(`term_loc_hybrid_data.${primaryKey}`, newVal);
      if (fallbackKey && ld[fallbackKey]) {
        updateData(`term_loc_hybrid_data.${fallbackKey}`, '');
      }
    };
    return (
      <div>
        <Label htmlFor={`edit-${primaryKey}`}>{label}</Label>
        <Input
          id={`edit-${primaryKey}`}
          type={type || 'text'}
          value={val}
          onChange={(e) => handleChange(e.target.value)}
          data-testid={`edit-${primaryKey}`}
        />
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Loan Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('use_of_proceeds', 'Use of Proceeds')}
            {mergedField('total_loan_amount', 'funding_amount_requested', 'Loan Amount', 'number')}
            {field('use_of_funds', 'Use of Funds')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Business Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mergedField('business_name', 'legal_corporate_name', 'Business Name')}
            {field('doing_business_as', 'DBA')}
            {mergedField('business_structure', 'entity_type', 'Business Structure')}
            {mergedField('average_annual_revenue', 'annual_revenue', 'Annual Revenue', 'number')}
            {field('monthly_revenue', 'Monthly Revenue', 'number')}
            {mergedField('date_established', 'business_start_date', 'Date Established')}
            {field('federal_tax_id', 'Federal Tax ID')}
            {field('business_phone', 'Business Phone')}
            {field('company_website', 'Company Website')}
            {mergedField('naics_code', 'industry_type', 'Industry / NAICS Code')}
            {field('state_of_incorporation', 'State of Incorporation')}
            {field('number_of_employees', 'Number of Employees', 'number')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Business Address</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('business_address', 'Business Address')}
            {field('business_city', 'City')}
            {field('business_state', 'State')}
            {field('business_zip', 'ZIP')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Role at Business</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('role_at_business', 'Role')}
            {field('title_at_company', 'Title')}
            {field('ownership_percentage', 'Ownership %', 'number')}
            {field('years_with_business', 'Years with Business', 'number')}
            {field('years_industry_experience', 'Years Industry Experience', 'number')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Personal Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('first_name', 'First Name')}
            {field('last_name', 'Last Name')}
            {field('email_address', 'Email')}
            {field('phone_number', 'Phone')}
            {mergedField('ssn_itin', 'social_security_number', 'SSN')}
            {field('date_of_birth', 'Date of Birth')}
            {field('place_of_birth', 'Place of Birth')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Identification</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('id_type', 'ID Type')}
            {field('id_number', 'ID Number')}
            {field('id_state_issued', 'State Issued')}
            {field('id_expiration_date', 'Expiration Date')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Home Address</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('owns_residence', 'Owns Residence')}
            {field('home_address', 'Home Address')}
            {field('home_city', 'City')}
            {field('home_state', 'State')}
            {field('home_zip', 'ZIP')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Other</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('citizenship_status', 'Citizenship Status')}
            {field('other_business_interests', 'Other Business Interests')}
          </div>
        </CardContent>
      </Card>
      <AdditionalOwnersEditor dataKey="term_loc_hybrid_data" data={data} updateData={updateData} />
    </div>
  );
};

const GrowthTermLoanEditForm = ({ data, updateData }) => {
  const field = makeField(data, 'term_loan_3_5yr_data', updateData);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Business Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('business_name', 'Business Name')}
            {field('doing_business_as', 'DBA')}
            {field('business_structure', 'Business Structure')}
            {field('average_annual_revenue', 'Avg Annual Revenue', 'number')}
            {field('date_established', 'Date Established')}
            {field('federal_tax_id', 'Federal Tax ID')}
            {field('business_phone', 'Business Phone')}
            {field('naics_code', 'NAICS Code')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Business Address</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('business_address', 'Address')}
            {field('business_city', 'City')}
            {field('business_state', 'State')}
            {field('business_zip', 'ZIP')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Your Role</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('role_at_business', 'Role')}
            {field('title_at_company', 'Title')}
            {field('ownership_percentage', 'Ownership %', 'number')}
            {field('years_with_business', 'Years with Business', 'number')}
            {field('years_industry_experience', 'Years Industry Experience', 'number')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Personal Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('first_name', 'First Name')}
            {field('last_name', 'Last Name')}
            {field('email_address', 'Email')}
            {field('phone_number', 'Phone')}
            {field('ssn_itin', 'SSN/ITIN')}
            {field('date_of_birth', 'Date of Birth')}
            {field('place_of_birth', 'Place of Birth')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Identification</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('id_type', 'ID Type')}
            {field('id_number', 'ID Number')}
            {field('id_state_issued', 'State Issued')}
            {field('id_expiration_date', 'Expiration Date')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Home Address</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('owns_residence', 'Owns Residence')}
            {field('home_address', 'Address')}
            {field('home_city', 'City')}
            {field('home_state', 'State')}
            {field('home_zip', 'ZIP')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Other</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('citizenship_status', 'Citizenship Status')}
            {field('other_business_interests', 'Other Business Interests')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Loan Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('use_of_proceeds', 'Use of Proceeds')}
            {field('total_loan_amount', 'Total Loan Amount', 'number')}
          </div>
        </CardContent>
      </Card>
      <AdditionalOwnersEditor dataKey="term_loan_3_5yr_data" data={data} updateData={updateData} />
    </div>
  );
};

const MerchantProcessingEditForm = ({ data, updateData }) => {
  const field = makeField(data, 'merchant_processing_data', updateData);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Contact Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('contact_name', 'Contact Name')}
            {field('company_name', 'Company Name')}
            {field('industry_type', 'Industry Type')}
            {field('website_url', 'Website URL')}
            {field('years_in_business', 'Years in Business')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Point of Sale Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('pos_software_or_terminals', 'POS Software or Terminals')}
            {field('pos_software_name', 'POS Software Name')}
            {field('number_of_terminals', 'Number of Terminals', 'number')}
            {field('terminal_brand_model', 'Terminal Brand and Model')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const JumboTermLoanEditForm = ({ data, updateData }) => {
  const field = makeField(data, 'jumbo_term_loan_data', updateData);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Business Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('business_legal_name', 'Business Legal Name')}
            {field('business_dba_name', 'DBA Name')}
            {field('type_of_business_entity', 'Business Entity Type')}
            {field('industry_type', 'Industry Type')}
            {field('state_of_incorporation', 'State of Incorporation')}
            {field('business_street_address', 'Street Address')}
            {field('business_apt_suite', 'Apt/Suite')}
            {field('business_city', 'City')}
            {field('business_state', 'State')}
            {field('business_zip', 'ZIP')}
            {field('amount_requested', 'Amount Requested', 'number')}
            {field('business_federal_tax_id', 'Federal Tax ID')}
            {field('business_start_date', 'Business Start Date')}
            {field('website', 'Website')}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Owner Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('owner_first_name', 'First Name')}
            {field('owner_last_name', 'Last Name')}
            {field('owner_dob', 'Date of Birth')}
            {field('owner_street_address', 'Street Address')}
            {field('owner_apt_suite', 'Apt/Suite')}
            {field('owner_city', 'City')}
            {field('owner_state', 'State')}
            {field('owner_zip', 'ZIP')}
            {field('owner_ssn', 'SSN')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const TransportationFactoringEditForm = ({ data, updateData }) => {
  const field = makeField(data, 'transportation_factoring_data', updateData);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Applicant Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('first_name', 'First Name')}
            {field('last_name', 'Last Name')}
            {field('mc_number', 'MC Number')}
            {field('email', 'Email')}
            {field('phone_number', 'Phone Number')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Field definitions for product types handled by GenericEditForm
const GENERIC_FIELD_DEFS = {
  merchant_processing_data: [
    { key: 'contact_name', label: 'Contact Name' },
    { key: 'company_name', label: 'Company Name' },
    { key: 'industry_type', label: 'Industry Type' },
    { key: 'website_url', label: 'Website URL' },
    { key: 'years_in_business', label: 'Years in Business' },
    { key: 'monthly_volume', label: 'Monthly Volume', type: 'number' },
    { key: 'pos_software_or_terminals', label: 'POS Software or Terminals' },
    { key: 'pos_software_name', label: 'POS Software Name' },
    { key: 'number_of_terminals', label: 'Number of Terminals', type: 'number' },
    { key: 'terminal_brand_model', label: 'Terminal Brand and Model' },
  ],
  jumbo_term_loan_data: [
    { key: 'business_legal_name', label: 'Business Legal Name' },
    { key: 'business_dba_name', label: 'Business DBA Name' },
    { key: 'type_of_business_entity', label: 'Type of Business Entity' },
    { key: 'industry_type', label: 'Industry Type' },
    { key: 'state_of_incorporation', label: 'State of Incorporation' },
    { key: 'business_street_address', label: 'Business Street Address' },
    { key: 'business_apt_suite', label: 'Apartment, Suite, Room' },
    { key: 'business_city', label: 'City' },
    { key: 'business_state', label: 'State' },
    { key: 'business_zip', label: 'ZIP' },
    { key: 'amount_requested', label: 'Amount Requested' },
    { key: 'business_federal_tax_id', label: 'Business Federal Tax ID' },
    { key: 'business_start_date', label: 'Business Start Date' },
    { key: 'website', label: 'Website' },
    { key: 'owner_first_name', label: 'Owner First Name' },
    { key: 'owner_last_name', label: 'Owner Last Name' },
    { key: 'owner_dob', label: 'Date of Birth' },
    { key: 'owner_street_address', label: 'Home Street Address' },
    { key: 'owner_apt_suite', label: 'Apartment, Suite, Room' },
    { key: 'owner_city', label: 'City' },
    { key: 'owner_state', label: 'State' },
    { key: 'owner_zip', label: 'ZIP' },
    { key: 'owner_ssn', label: 'Social Security #' },
  ],
  transportation_factoring_data: [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'mc_number', label: 'MC Number' },
    { key: 'email', label: 'Email' },
    { key: 'phone_number', label: 'Phone Number' },
  ],
  debt_consulting_data: [
    // Business Information
    { key: 'legal_business_name', label: 'Legal Business Name' },
    { key: 'dba', label: 'Doing Business As (DBA)' },
    { key: 'business_phone', label: 'Business Phone' },
    { key: 'business_email', label: 'Business Email' },
    { key: 'business_street_address', label: 'Business Street Address' },
    { key: 'business_city', label: 'City' },
    { key: 'business_state', label: 'State' },
    { key: 'business_zip', label: 'Zip' },
    { key: 'business_start_date', label: 'Business Start Date' },
    { key: 'business_type_services', label: 'Business Type / Services Provided', type: 'textarea' },
    { key: 'legal_structure', label: 'Legal Structure' },
    { key: 'landlord_name', label: 'Landlord Name' },
    { key: 'landlord_phone', label: 'Landlord Phone' },
    // Financial Information
    { key: 'federal_tax_id', label: 'Federal Tax ID / EIN' },
    { key: 'state_of_incorporation', label: 'State of Incorporation' },
    { key: 'requested_funding_amount', label: 'Requested Funding Amount', type: 'number' },
    { key: 'purpose_of_funds', label: 'Purpose of Funds', type: 'textarea' },
    // Advances & Debts
    { key: 'has_merchant_advances', label: 'Do you currently have any merchant cash advances or business loans outstanding?' },
    { key: 'has_other_business_debts', label: 'Do you have any other outstanding business debts?' },
    // Note: individual debt rows are rendered by the AIApplicationFlow's
    // repeater UI (unlimited). This flat list is kept for the legacy
    // per-field editor only; if you're editing an app here and it has
    // structured debts, use the main application flow.
    // Ownership - Primary Owner
    { key: 'owner1_first_name', label: 'Owner 1 — First Name' },
    { key: 'owner1_last_name', label: 'Owner 1 — Last Name' },
    { key: 'owner1_email', label: 'Owner 1 — Email' },
    { key: 'owner1_phone', label: 'Owner 1 — Phone' },
    { key: 'owner1_date_of_birth', label: 'Owner 1 — Date of Birth' },
    { key: 'owner1_ownership_percent', label: 'Owner 1 — Ownership %', type: 'number' },
    { key: 'owner1_home_street_address', label: 'Owner 1 — Home Street Address' },
    { key: 'owner1_home_city', label: 'Owner 1 — City' },
    { key: 'owner1_home_state', label: 'Owner 1 — State' },
    { key: 'owner1_home_zip', label: 'Owner 1 — Zip' },
    { key: 'has_co_owner', label: 'Is there a co-owner?' },
  ],
  term_loc_hybrid_data: [
    { key: 'legal_corporate_name', label: 'Legal Corporate Name' },
    { key: 'business_name', label: 'Business DBA Name' },
    { key: 'business_address', label: 'Business Address' },
    { key: 'funding_amount_requested', label: 'Funding Amount Requested' },
  ],
  real_estate_financing_data: [
    // --- Request for Financing ---
    { key: 'financing_amount_requested', label: 'Financing Amount Requested', type: 'number' },
    { key: 'down_payment_amount', label: 'Down Payment Amount', type: 'number' },
    { key: 'detailed_use_of_funds', label: 'Detailed Use of Funds', type: 'textarea', hint: 'Please be as detailed as possible in explaining the scenario.' },
    // --- Business Information ---
    { key: 'legal_name_of_business', label: 'Legal Name of Business' },
    { key: 'business_in_good_standing', label: 'Business in Good Standing?', type: 'select', options: ['Yes', 'No'] },
    { key: 'date_established', label: 'Date Established' },
    { key: 'tax_id', label: 'Tax ID (EIN)' },
    { key: 'business_type', label: 'Business Type', type: 'select', options: ['Corp', 'LLC', 'Sole-Proprietor'] },
    { key: 'business_address', label: 'Business Address' },
    { key: 'business_city_state_zip', label: 'Business City, State, ZIP' },
    { key: 'mailing_address', label: 'Mailing Address' },
    { key: 'mailing_city_state_zip', label: 'Mailing City, State, ZIP' },
    { key: 'main_phone', label: 'Main Phone' },
    { key: 'fax', label: 'Fax' },
    { key: 'number_of_employees', label: '# of Employees', type: 'number' },
    { key: 'business_email', label: 'Business Email' },
    { key: 'type_of_business', label: 'Type of Business' },
    // --- Pre-Qualification Intake (new — all optional) ---
    // Borrower Information
    { key: 'borrower_first_name', label: 'Borrower First Name' },
    { key: 'borrower_last_name', label: 'Borrower Last Name' },
    { key: 'borrowers_name', label: "Borrower's Full Name (legacy)" },
    { key: 'borrower_address', label: 'Borrower Street Address' },
    { key: 'borrower_city', label: 'Borrower City' },
    { key: 'borrower_state', label: 'Borrower State' },
    { key: 'borrower_zip', label: 'Borrower Zip' },
    { key: 'borrower_date_of_birth', label: 'Borrower Date of Birth' },
    { key: 'borrower_ssn', label: 'Borrower Social Security Number' },
    { key: 'borrower_email', label: 'Borrower Email' },
    { key: 'borrower_phone', label: 'Borrower Contact Number' },
    { key: 'borrower_citizenship_status', label: 'Borrower Citizenship Status', type: 'select', options: ['US Citizen', 'Legal Permanent Resident', 'Foreign National'] },
    // Property Information
    { key: 'property_address', label: 'Property Address' },
    { key: 'property_type', label: 'Property Type' },
    { key: 'number_of_units', label: 'Number of Units', type: 'number' },
    { key: 'total_sqft', label: 'Total SQFT', type: 'number' },
    { key: 'purchase_price', label: 'Purchase Price', type: 'number' },
    { key: 'property_under_contract', label: 'Property Under Contract?', type: 'select', options: ['Yes', 'No'] },
    { key: 'original_purchase_price', label: 'Original Purchase Price (if already owned)', type: 'number' },
    { key: 'original_purchase_date', label: 'Original Purchase Date (if already owned)' },
    { key: 'existing_payoff_amount', label: 'Existing Payoff Amount (if already owned)', type: 'number' },
    { key: 'as_is_value', label: 'As-Is Value', type: 'number' },
    { key: 'rehab_budget', label: 'Rehab Budget', type: 'number' },
    { key: 'estimated_after_repair_value', label: 'Estimated After Repair Value (ARV)', type: 'number' },
    { key: 'liquidity_cash_to_close', label: 'Liquidity / Cash to Close', type: 'number' },
    { key: 'exit_strategy', label: 'Exit Strategy', type: 'select', options: ['Sell', 'Refinance'] },
    { key: 'monthly_rental_income', label: 'Monthly Rental Income (if Refinance)', type: 'number' },
    { key: 'annual_taxes', label: 'Annual Taxes', type: 'number' },
    { key: 'annual_insurance', label: 'Annual Insurance', type: 'number' },
    { key: 'estimated_middle_fico', label: 'Estimated Middle FICO Score', type: 'number' },
    { key: 'completed_rehab_projects_3yr', label: 'Completed Rehab Projects (Past 3 Years)', type: 'number' },
    { key: 'additional_notes', label: 'Additional Notes regarding the loan/borrower', type: 'textarea' },
  ],
};

const GenericEditForm = ({ data, dataKey, updateData }) => {
  const fieldDefs = GENERIC_FIELD_DEFS[dataKey];

  const renderField = ({ key, label, type, options, placeholder }) => {
    const value = data?.[key];
    const id = `edit-${key}`;
    if (type === 'select') {
      return (
        <select
          id={id}
          value={value ?? ''}
          onChange={(e) => updateData(`${dataKey}.${key}`, e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          data-testid={id}
        >
          <option value="">Select...</option>
          {(options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    if (type === 'textarea') {
      return (
        <textarea
          id={id}
          rows={4}
          value={value || ''}
          onChange={(e) => updateData(`${dataKey}.${key}`, e.target.value)}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          data-testid={id}
          placeholder={placeholder || label}
        />
      );
    }
    return (
      <Input
        id={id}
        type={type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={(e) => updateData(`${dataKey}.${key}`, type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
        data-testid={id}
      />
    );
  };

  if (fieldDefs) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Application Data</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fieldDefs.map((def) => (
              <div key={def.key} className={def.type === 'textarea' ? 'md:col-span-2' : ''}>
                <Label htmlFor={`edit-${def.key}`}>{def.label}</Label>
                {renderField(def)}
                {def.hint && (
                  <p className="mt-1 text-xs text-slate-500 italic" data-testid={`edit-${def.key}-hint`}>{def.hint}</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fallback: render whatever keys exist in data
  if (!data || typeof data !== 'object') return null;
  const entries = Object.entries(data).filter(([k, v]) => typeof v !== 'object' || v === null);
  if (entries.length === 0) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Application Data</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {entries.map(([key, val]) => (
            <div key={key}>
              <Label htmlFor={`edit-${key}`}>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Label>
              <Input
                id={`edit-${key}`}
                value={val || ''}
                onChange={(e) => updateData(`${dataKey}.${key}`, e.target.value)}
                data-testid={`edit-${key}`}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};